import type { PlaygroundJoinGameOptions } from '@gotti/test-utils';
import type { PlaygroundEnvironmentDefinition } from '../types.js';
import { toArray } from '../util/arrays.js';

/**
 * Refuses an `environment.offline` that is incomplete or contradicts itself, before anything boots — an
 * offline definition is never skipped as an incomplete environment. It needs its `clientGame`, and its
 * `serverGame` for the game data every client joins with. A client-authoritative boot runs zero
 * AreaServers and says so with `serverAreas: []` (an omitted list is refused like a non-empty one), and
 * nothing may ask to join a server area: not `joinGame.initialAreas` (at either clientConfig layer), not
 * an `initClients` entry with a `joinGame` of its own (its clients are declared through `clientConfig`).
 *
 * Shared by the boot (`runDefaultSetup`), a pair's offline side (`resolvePairVariant`) and a single
 * definition's node selection (`selectPlaygroundNode`), so a pair is refused at load, and a definition when it
 * is selected, with exactly the message its boot would fail with. It lives apart from the runtime so the
 * node-side loader can call it without importing the page's boot.
 */
export const refuseContradictoryOffline = (envDef: PlaygroundEnvironmentDefinition, declaredJoin: PlaygroundJoinGameOptions | undefined): void => {
  if (!envDef.clientGame) {
    throw new Error('[gotti][playground] environment.offline needs environment.clientGame: it is the game every client starts.');
  }
  if (!envDef.serverGame) {
    throw new Error("[gotti][playground] environment.offline needs environment.serverGame: the game's type and data every client joins with come from it, though no server runs it.");
  }
  if (!Array.isArray(envDef.serverAreas) || envDef.serverAreas.length > 0) {
    throw new Error('[gotti][playground] environment.offline needs serverAreas: [] — a client-authoritative boot runs zero AreaServers; list the areas under offline.initAreas.');
  }
  if (declaredJoin?.initialAreas) {
    throw new Error('[gotti][playground] clientConfig.joinGame.initialAreas cannot be combined with environment.offline: there is no server area to join. Use offline.initAreas.');
  }
  if (toArray(envDef.initClients).some((config) => config?.joinGame)) {
    throw new Error('[gotti][playground] environment.initClients cannot declare joinGame under environment.offline: a client-authoritative boot has no server area to join. Declare the clients through clientConfig (numberOfClients, joinGame) for an offline boot.');
  }
};
